"use client";

import { useEffect, useState, useRef } from "react";
import { ArrowUp, ArrowDown, ChevronLeft, ChevronRight } from "lucide-react";

interface MarketItem {
  label: string;
  value: number;
  change: number;
  type: "index" | "commodity" | "currency";
  suffix?: string;
  rawSymbol?: string;
}

export default function MarketTicker() {
  const [indices, setIndices] = useState<MarketItem[]>([]);
  const [commodities, setCommodities] = useState<MarketItem[]>([]);
  const [commodityIndex, setCommodityIndex] = useState(0);
  const [rates, setRates] = useState<Record<string, number>>({});
  const [localCurrency, setLocalCurrency] = useState("USD");
  const [loading, setLoading] = useState(true);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    // 🌍 HIGH-RELIABILITY LOCALIZATION (Timezone + Locale)
    try {
      const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;

      // Strict mapping for major regions to ensure 100% correct currency
      if (tz.includes("Kolkata") || tz.includes("Calcutta") || tz.includes("India")) {
        setLocalCurrency("INR");
      } else if (tz.includes("London") || tz.includes("Europe/London") || tz.includes("GB")) {
        setLocalCurrency("GBP");
      } else if (tz.includes("Australia") || tz.includes("Sydney") || tz.includes("Melbourne")) {
        setLocalCurrency("AUD");
      } else if (tz.includes("Europe") && !tz.includes("London")) {
        setLocalCurrency("EUR");
      } else if (tz.includes("Tokyo") || tz.includes("Japan")) {
        setLocalCurrency("JPY");
      } else if (tz.includes("America") || tz.includes("US")) {
        setLocalCurrency("USD");
      } else {
        // Fallback to browser detected currency, default to USD
        const detectedCurrency = Intl.NumberFormat().resolvedOptions().currency;
        setLocalCurrency(detectedCurrency || "USD");
      }
    } catch (e) {
      setLocalCurrency("USD");
    }

    const fetchMarketData = async () => {
      try {
        const res = await fetch("/api/market");
        if (!res.ok) throw new Error("Network response was not ok");
        const json = await res.json();

        if (json.data) {
          const items: MarketItem[] = json.data;

          // 🏛️ STRICT SORTING FOR MARKETS (Indices)
          const indicesOrder = ["NIFTY 50", "SENSEX", "NASDAQ", "S&P 500"];
          const sortedIndices = items
            .filter(i => i.type === "index")
            .sort((a, b) => indicesOrder.indexOf(a.label) - indicesOrder.indexOf(b.label));

          // 🛠️ PRODUCTS (Commodities/Crypto)
          const productsOrder = ["OIL (CRUDE)", "GOLD", "PLATINUM", "BITCOIN"];
          const sortedProducts = items
            .filter(i => i.type === "commodity")
            .sort((a, b) => productsOrder.indexOf(a.label) - productsOrder.indexOf(b.label));

          setIndices(sortedIndices);
          setCommodities(sortedProducts);
          if (json.rates) setRates(json.rates);
        }
      } catch (error) {
        console.error("Failed to fetch market data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchMarketData();
    const interval = setInterval(fetchMarketData, 300000); // 🚀 HIGH-FIDELITY: 5-minute refresh for latest commodity prices
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    if (commodities.length > 0) {
      const interval = setInterval(() => {
        setCommodityIndex((prev) => (prev + 1) % commodities.length);
      }, 4000);
      return () => clearInterval(interval);
    }
  }, [commodities]);

  const formatCurrency = (val: number, isCommodity: boolean, label: string) => {
    const rate = localCurrency === "USD" ? 1 : rates[localCurrency] || 1;
    const localizedValue = val * rate;

    return new Intl.NumberFormat(undefined, {
      style: "currency",
      currency: localCurrency,
      maximumFractionDigits: label === "BITCOIN" ? 0 : 2,
    }).format(localizedValue);
  };

  const scroll = (direction: "left" | "right") => {
    if (scrollRef.current) {
      const container = scrollRef.current;
      const scrollAmount = 250;
      container.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth"
      });
    }
  };

  if (loading && indices.length === 0) {
    return (
      <div className="flex items-center gap-4 py-1.5 px-2 animate-pulse bg-background/50 border-y border-border/10">
        <div className="h-3 w-32 bg-muted rounded" />
        <div className="h-3 w-32 bg-muted rounded" />
        <div className="h-3 w-32 bg-muted rounded ml-auto" />
      </div>
    );
  }

  const activeCommodity = commodities[commodityIndex];

  return (
    <div className="relative flex items-center w-full bg-background/50 border-y border-border/10 overflow-hidden group/ticker">

      {/* 🕹️ NAVIGATION CONTROLS */}
      <div className="absolute left-0 z-40 flex gap-0.5 opacity-0 group-hover/ticker:opacity-100 transition-all duration-300">
        <button
          onClick={() => scroll("left")}
          className="p-1.5 bg-background/90 backdrop-blur-md border border-border/30 rounded-l-sm hover:bg-brand hover:text-white transition-colors shadow-sm"
        >
          <ChevronLeft className="w-3 h-3" />
        </button>
        <button
          onClick={() => scroll("right")}
          className="p-1.5 bg-background/90 backdrop-blur-md border border-border/30 rounded-r-sm hover:bg-brand hover:text-white transition-colors shadow-sm"
        >
          <ChevronRight className="w-3 h-3" />
        </button>
      </div>

      {/* 📈 SCROLLING INDICES */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-x-auto no-scrollbar h-7 flex items-center relative scroll-smooth selection:bg-transparent"
      >
        <div className="flex items-center gap-12 pl-12 pr-12 animate-marquee whitespace-nowrap">
          {[...indices, ...indices, ...indices].map((item, idx) => (
            <div key={`${item.label}-${idx}`} className="flex items-center gap-2.5 group/item cursor-default shrink-0">
              <span className="text-[11px] font-bold text-muted-foreground/60 uppercase group-hover/item:text-brand transition-colors whitespace-nowrap">
                {item.label}
              </span>
              <span className="text-[11px] font-black font-mono whitespace-nowrap">
                {item.value.toLocaleString(undefined, { maximumFractionDigits: 2 })}
              </span>
              <span className={`flex items-center text-[10px] font-bold font-mono ${item.change >= 0 ? "text-green-600" : "text-red-500"}`}>
                {item.change >= 0 ? <ArrowUp className="w-2.5 h-2.5 mr-0.5" /> : <ArrowDown className="w-2.5 h-2.5 mr-0.5" />}
                {Math.abs(item.change).toFixed(2)}%
              </span>
            </div>
          ))}
        </div>
        <div className="absolute inset-y-0 left-0 w-16 bg-gradient-to-r from-background to-transparent z-10 pointer-events-none" />
        <div className="absolute inset-y-0 right-0 w-16 bg-gradient-to-l from-background to-transparent z-10 pointer-events-none" />
      </div>

      {/* 🔄 ROTATING COMMODITIES/CRYPTO */}
      <div className="w-[150px] md:w-[200px] shrink-0 h-7 flex items-center justify-end px-4 border-l border-border/20 bg-muted/5 z-30 backdrop-blur-sm shadow-[-4px_0_8px_-4px_rgba(0,0,0,0.1)]">
        {activeCommodity && (
          <div key={`${activeCommodity.label}-${commodityIndex}`} className="flex items-center gap-3 animate-in fade-in slide-in-from-right-4 duration-500">
            <span className="text-[10px] font-black text-brand uppercase tracking-widest whitespace-nowrap">
              {activeCommodity.label}
            </span>
            <div className="flex flex-col items-end">
              <span className="text-[11px] font-black font-mono leading-none whitespace-nowrap text-right">
                {formatCurrency(activeCommodity.value, true, activeCommodity.label)}
                <span className="text-[8px] font-normal opacity-40 ml-0.5">{activeCommodity.suffix}</span>
              </span>
              <span className={`text-[8px] font-bold font-mono ${activeCommodity.change >= 0 ? "text-green-600" : "text-red-500"}`}>
                {activeCommodity.change >= 0 ? "+" : ""}{activeCommodity.change}%
              </span>
            </div>
          </div>
        )}
      </div>

      <style jsx global>{`
        @keyframes marquee {
          0% { transform: translateX(0); }
          100% { transform: translateX(-33.33%); }
        }
        .animate-marquee {
          animation: marquee 60s linear infinite;
        }
        .animate-marquee:hover {
          animation-play-state: paused;
        }
        .no-scrollbar::-webkit-scrollbar {
          display: none;
        }
        .no-scrollbar {
          -ms-overflow-style: none;
          scrollbar-width: none;
        }
      `}</style>
    </div>
  );
}
